import React from "react";
import { Modal, Button, Space } from "antd";
import type { ModalProps } from "antd";

// Footer 按钮配置接口
export interface FooterButtonConfig {
  key: string;
  text: string;
  type?: "default" | "primary" | "dashed" | "link" | "text" | "danger";
  icon?: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

// 自定义内边距类型：支持统一值、字符串，或分别指定上下左右/水平垂直
type PaddingSpec =
  | number
  | string
  | {
      top?: number | string;
      right?: number | string;
      bottom?: number | string;
      left?: number | string;
      vertical?: number | string; // 上下
      horizontal?: number | string; // 左右
    };

// ContainerModal 属性接口
export interface ContainerModalProps {
  // Modal 基础属性
  title?: string | React.ReactNode;
  visible: boolean;
  width?: number | string; // 默认 800，支持数字或字符串（如 "auto"）
  height?: number | string; // 默认 600，用于整个 modal 的高度（包括 header 和 footer）

  // 内容
  children: React.ReactNode;

  // Footer 按钮配置
  footerLeftButtons?: FooterButtonConfig[] | React.ReactNode[]; // 左侧按钮
  footerRightButtons?: FooterButtonConfig[] | React.ReactNode[]; // 右侧按钮（最多两个）

  // 样式相关
  /**
   * 内容内边距（应用在 Modal body）
   * - 数字：等同于 `px`，如 16 -> "16px"
   * - 字符串：直接作为 CSS padding 传入，例如 "8px 16px"
   * - 对象：可分别指定上下左右或水平/垂直
   */
  contentPadding?: PaddingSpec; // 默认 16

  // 关闭回调
  onCancel: () => void;

  // 标题点击回调
  onTitleClick?: () => void;

  // Modal 其他属性
  modalProps?: Omit<
    ModalProps,
    "title" | "open" | "width" | "onCancel" | "footer"
  >;
}

/**
 * 通用模态框容器组件
 * 支持 footer 左侧按钮和右侧两个按钮，支持自定义宽高和内容内边距
 */
const ContainerModal: React.FC<ContainerModalProps> = ({
  title,
  visible,
  width = 800,
  height = 600,
  children,
  footerLeftButtons = [],
  footerRightButtons = [],
  contentPadding = 16,
  onCancel,
  onTitleClick,
  modalProps = {},
}) => {
  // 渲染左侧按钮
  const renderLeftButtons = (): React.ReactNode => {
    if (!footerLeftButtons || footerLeftButtons.length === 0) {
      return null;
    }

    // 如果是 React 节点数组，直接渲染
    if (
      Array.isArray(footerLeftButtons) &&
      footerLeftButtons.length > 0 &&
      React.isValidElement(footerLeftButtons[0])
    ) {
      return <Space>{footerLeftButtons as React.ReactNode[]}</Space>;
    }

    // 如果是按钮配置数组
    return (
      <Space>
        {(footerLeftButtons as FooterButtonConfig[]).map((button) => {
          if (button.hidden) return null;

          const buttonType = button.type === "danger" ? "default" : button.type;
          return (
            <Button
              key={button.key}
              type={buttonType}
              danger={button.type === "danger"}
              icon={button.icon}
              loading={button.loading}
              disabled={button.disabled}
              onClick={button.onClick}
            >
              {button.text}
            </Button>
          );
        })}
      </Space>
    );
  };

  // 渲染右侧按钮（最多两个）
  const renderRightButtons = (): React.ReactNode => {
    if (!footerRightButtons || footerRightButtons.length === 0) {
      return null;
    }

    // 限制最多两个按钮
    const buttonsToRender = footerRightButtons.slice(0, 2);

    // 如果是 React 节点数组，直接渲染
    if (
      Array.isArray(buttonsToRender) &&
      buttonsToRender.length > 0 &&
      React.isValidElement(buttonsToRender[0])
    ) {
      return <Space>{buttonsToRender as React.ReactNode[]}</Space>;
    }

    // 如果是按钮配置数组
    return (
      <Space>
        {(buttonsToRender as FooterButtonConfig[]).map((button) => {
          if (button.hidden) return null;

          const buttonType = button.type === "danger" ? "default" : button.type;
          return (
            <Button
              key={button.key}
              type={buttonType}
              danger={button.type === "danger"}
              icon={button.icon}
              loading={button.loading}
              disabled={button.disabled}
              onClick={button.onClick}
            >
              {button.text}
            </Button>
          );
        })}
      </Space>
    );
  };

  // 构建 Footer
  const renderFooter = (): React.ReactNode | null => {
    const leftButtons = renderLeftButtons();
    const rightButtons = renderRightButtons();

    // 如果没有任何按钮，返回 null（隐藏 footer）
    if (!leftButtons && !rightButtons) {
      return null;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>{leftButtons}</div>
        <div>{rightButtons}</div>
      </div>
    );
  };

  // 构建 Modal body 样式
  // 计算 body 高度：总高度减去 header（约 55px）和 footer（约 53px）
  const bodyHeight =
    typeof height === "number" ? Math.max(height - 55 - 53, 0) : undefined;
  const toCssUnit = (v: number | string | undefined): string | undefined =>
    typeof v === "number" ? `${v}px` : v;
  const resolvePadding = (p: PaddingSpec | undefined): string | undefined => {
    if (p === undefined) return undefined;
    if (typeof p === "number" || typeof p === "string") {
      return toCssUnit(p);
    }
    const t = toCssUnit(p.top ?? p.vertical);
    const r = toCssUnit(p.right ?? p.horizontal);
    const b = toCssUnit(p.bottom ?? p.vertical);
    const l = toCssUnit(p.left ?? p.horizontal);
    return [t ?? "0", r ?? "0", b ?? "0", l ?? "0"].join(" ");
  };
  const bodyStyle: React.CSSProperties = {
    padding: resolvePadding(contentPadding ?? 16),
    height: bodyHeight === undefined ? undefined : `${bodyHeight}px`,
    overflow: "auto",
    ...modalProps.styles?.body,
  };

  // 处理标题点击事件
  const renderTitle = (): React.ReactNode => {
    if (title && onTitleClick) {
      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onTitleClick();
        }
      };

      return (
        <button
          type="button"
          onClick={onTitleClick}
          onKeyDown={handleKeyDown}
          style={{
            cursor: "pointer",
            userSelect: "none",
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            font: "inherit",
            color: "inherit",
            textAlign: "inherit",
            width: "100%",
          }}
        >
          {title}
        </button>
      );
    }
    
    return title;
  };

  return (
    <Modal
      title={renderTitle()}
      open={visible}
      width={width}
      onCancel={onCancel}
      footer={renderFooter()}
      styles={{
        ...modalProps.styles,
        body: bodyStyle,
        header: {
          ...modalProps.styles?.header,
        },
        footer: {
          ...modalProps.styles?.footer,
        },
      }}
      style={{
        ...(typeof height === "string" ? { height } : {}),
        ...modalProps.style,
      }}
      {...modalProps}
    >
      {children}
    </Modal>
  );
};

export default ContainerModal;
